import moment from 'moment-timezone';

import {
  createJiraWorklog,
  CreateWorklogOption,
  getIssueWorklogs,
} from '../services/jira';
import { JiraWorklog } from '../types';
import { TogglTimeEntry } from '../types/toggl-api';

const JIRA_DATETIME_FORMAT = 'YYYY-MM-DDTHH:mm:ss.000ZZ';

export enum ADD_WORKLOG_ERROR_REASONS {
  UNEXPECTED_ERROR,
  WRONG_ISSUE_KEY,
}

export type ComparableWorklog = Omit<CreateWorklogOption, 'comment'> & {
  comment: string;
};

function convertToJiraDateTimeFormat(dateTime: string) {
  return moment(dateTime).format(JIRA_DATETIME_FORMAT);
}

function prepareWorklogEntry(
  worklogEntry: CreateWorklogEntry
): ComparableWorklog {
  return {
    ...worklogEntry,
    issueKey: worklogEntry.issueKey,
    timeSpentSeconds: worklogEntry.roundTime
      ? roundTime(worklogEntry.timeEntry.duration)
      : worklogEntry.timeEntry.duration,
    started: adjustTimeZone(worklogEntry),
    comment: worklogEntry.timeEntry.description,
  };
}

function adjustTimeZone(entry: CreateWorklogEntry) {
  if (entry.jiraTimeZone && entry.togglTimeZone) {
    const start = moment(entry.timeEntry.start)
      .utc()
      .format('YYYY-MM-DDTHH:mm:ss');
    const tmp = moment
      .tz(start, entry.togglTimeZone)
      .format(JIRA_DATETIME_FORMAT);
    const started = moment.tz(tmp, entry.jiraTimeZone);

    return started.clone().tz(entry.togglTimeZone).format(JIRA_DATETIME_FORMAT);
  }
  return convertToJiraDateTimeFormat(entry.timeEntry.start);
}

function adjustApiWorklog(worklog: JiraWorklog) {
  return {
    ...worklog,
    started: convertToJiraDateTimeFormat(worklog.started),
  };
}

export function makeComparableWorklogString({
  timeSpentSeconds,
  comment,
  started,
}: Pick<ComparableWorklog, 'comment' | 'timeSpentSeconds' | 'started'>) {
  const comparableTimeSpentSeconds = timeSpentSeconds - (timeSpentSeconds % 60);
  return `${comment}:${started}:${comparableTimeSpentSeconds}`;
}

export async function findIssueWorklog(worklog: ComparableWorklog) {
  const issueWorklogsResponse = await getIssueWorklogs(worklog.issueKey);
  if (Array.isArray(issueWorklogsResponse)) {
    const comparableWorklog = makeComparableWorklogString(worklog);
    return issueWorklogsResponse.find(
      (w) =>
        makeComparableWorklogString(adjustApiWorklog(w)) === comparableWorklog
    );
  }
  return issueWorklogsResponse.error;
}

interface CreateWorklogEntry {
  timeEntry: TogglTimeEntry;
  issueKey: string;
  togglTimeZone: string;
  jiraTimeZone?: string;
  roundTime: boolean;
}

export async function createWorklog(
  entry: CreateWorklogEntry
): CreateWorklogResponse {
  const preparedWorklogEntry = prepareWorklogEntry(entry);
  const foundWorklog = await findIssueWorklog(preparedWorklogEntry);
  if (foundWorklog && typeof foundWorklog !== 'object') {
    return { error: foundWorklog };
  }
  if (foundWorklog) return { ...entry, jiraWorklogId: foundWorklog.id };
  const createdWorklog = await createJiraWorklog(preparedWorklogEntry);
  return createdWorklog
    ? { ...entry, jiraWorklogId: createdWorklog.id }
    : { error: ADD_WORKLOG_ERROR_REASONS.UNEXPECTED_ERROR };
}

export interface CreatedWorklog extends CreateWorklogEntry {
  jiraWorklogId: string;
}

export type CreateWorklogResponse = Promise<
  CreatedWorklog | { error: ADD_WORKLOG_ERROR_REASONS }
>;

function roundTime(duration: number) {
  return duration % 60 > 29 ? duration + 60 : duration;
}
